/**
 * Signed playback token shared by Cloud Functions (issuer) and the Cloudflare
 * Worker playback gateway (verifier). Uses only WebCrypto so the same code
 * runs on Node 22 and Workers.
 *
 * Token grants time-limited GET access to every R2 object under a path
 * prefix (one dance's HLS directory). Playback URLs look like:
 *
 *   {mediaBase}/p/{token}/{objectKey}
 *
 * HLS playlists reference variants/segments with relative URIs, so the token
 * prefix carries through every request — no playlist rewriting needed.
 */
const encoder = new TextEncoder();
function base64UrlEncode(bytes) {
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function base64UrlDecode(text) {
    const base64 = text.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
async function hmacKey(secret) {
    return crypto.subtle.importKey("raw", encoder.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);
}
export async function signPlaybackToken(claims, secret) {
    const payload = base64UrlEncode(encoder.encode(JSON.stringify(claims)));
    const key = await hmacKey(secret);
    const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(payload));
    return `${payload}.${base64UrlEncode(new Uint8Array(signature))}`;
}
export async function verifyPlaybackToken(token, secret, nowUnixSeconds = Math.floor(Date.now() / 1000)) {
    const dot = token.indexOf(".");
    if (dot <= 0 || dot === token.length - 1)
        return { ok: false, reason: "malformed" };
    const payload = token.slice(0, dot);
    const signature = token.slice(dot + 1);
    let signatureBytes;
    let claims;
    try {
        signatureBytes = base64UrlDecode(signature);
        claims = JSON.parse(new TextDecoder().decode(base64UrlDecode(payload)));
    }
    catch {
        return { ok: false, reason: "malformed" };
    }
    const key = await hmacKey(secret);
    const valid = await crypto.subtle.verify("HMAC", key, signatureBytes, encoder.encode(payload));
    if (!valid)
        return { ok: false, reason: "bad-signature" };
    if (claims.v !== 1 ||
        typeof claims.exp !== "number" ||
        typeof claims.pfx !== "string" ||
        claims.pfx.length === 0) {
        return { ok: false, reason: "malformed" };
    }
    if (claims.exp <= nowUnixSeconds)
        return { ok: false, reason: "expired" };
    return { ok: true, claims };
}
/** Builds the token-prefixed playback URL for an object key. */
export function playbackUrl(mediaBase, token, objectKey) {
    return `${mediaBase.replace(/\/$/, "")}/p/${token}/${objectKey}`;
}
/**
 * Parses "/p/{token}/{objectKey}" from a request path. Returns null if the
 * path doesn't match.
 */
export function parsePlaybackPath(pathname) {
    const match = /^\/p\/([^/]+)\/(.+)$/.exec(pathname);
    if (!match)
        return null;
    return { token: match[1], objectKey: match[2] };
}
